import { todayIso } from '../dates'
import type { CrmLastActivity, CrmLastContact, CrmNextAction } from '../types'
import { LAST_CONTACT_LABELS } from './labels'

const TZ = 'Europe/Amsterdam'
// Fixed Dutch words, no `Intl` month names: ICU writes "okt." with a dot.
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec']
const MONTHS_LONG = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
]
const WEEKDAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za']
const DAY_MS = 86_400_000

/** Year, month (1–12) and day of an ISO date or the date part of a timestamp. */
function parts(iso: string): [number, number, number] {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number)
  return [year, month, day]
}

function utc(iso: string): number {
  const [year, month, day] = parts(iso)
  return Date.UTC(year, month - 1, day)
}

/** "13 okt"; with the year when it is not this year: "1 mrt 2025". */
export function shortDay(iso: string, today: string = todayIso()): string {
  const [year, month, day] = parts(iso)
  const label = `${day} ${MONTHS[month - 1]}`
  return year === parts(today)[0] ? label : `${label} ${year}`
}

/** "vr 16 okt" (Gepland). */
export function weekdayDay(iso: string, today: string = todayIso()): string {
  return `${WEEKDAYS[new Date(utc(iso)).getUTCDay()]} ${shortDay(iso, today)}`
}

/** "OKTOBER 2026": a month heading in the timeline. */
export function monthHeading(iso: string): string {
  const [year, month] = parts(iso)
  return `${MONTHS_LONG[month - 1].toUpperCase()} ${year}`
}

/** "maart 2025" (Relatie sinds), "—" when empty. */
export function monthYear(iso: string | null): string {
  if (!iso) return '—'
  const [year, month] = parts(iso)
  return `${MONTHS_LONG[month - 1]} ${year}`
}

/** "14:02": the time of a moment in Amsterdam. */
export function timeOfDay(moment: string): string {
  return new Date(moment).toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: TZ,
  })
}

/** The Amsterdam day of a moment: "2026-10-14" for 23:30 UTC on 13 Oct (ED-49). */
export function amsterdamDay(moment: string): string {
  return new Date(moment).toLocaleDateString('sv-SE', { timeZone: TZ })
}

export function addDays(iso: string, days: number): string {
  return new Date(utc(iso) + days * DAY_MS).toISOString().slice(0, 10)
}

/** Whole days from `from` to `to`. */
export function daysBetween(from: string, to: string): number {
  return Math.round((utc(to) - utc(from)) / DAY_MS)
}

/** "8 dagen te laat" / "1 dag te laat" for a date before today, else null (IA-20). */
export function overdueText(due: string, today: string = todayIso()): string | null {
  const late = daysBetween(due, today)
  if (late <= 0) return null
  return late === 1 ? '1 dag te laat' : `${late} dagen te laat`
}

/** "vandaag", "gisteren", "2 dgn geleden" …: the words of the Contacten list (relatiefNl). */
export function sinceText(iso: string, today: string = todayIso()): string {
  const days = daysBetween(iso, today)
  if (days <= 0) return 'vandaag'
  if (days === 1) return 'gisteren'
  if (days < 7) return `${days} dgn geleden`
  if (days < 31) return `${Math.floor(days / 7)} wkn geleden`
  if (days < 365) return `${Math.floor(days / 30)} mnd geleden`
  return `${Math.floor(days / 365)} jr geleden`
}

/** "Bewerkt 13 okt · Jesse" above "Over …". */
export function editedLine(at: string, by: string | null, today: string = todayIso()): string {
  const day = shortDay(amsterdamDay(at), today)
  return by ? `Bewerkt ${day} · ${by}` : `Bewerkt ${day}`
}

/** "2 dgn geleden · e-mail verstuurd"; "Nog geen contact" (addendum A.6). */
export function lastContactText(last: CrmLastContact | null, today: string = todayIso()): string {
  if (!last) return 'Nog geen contact'
  return `${sinceText(last.on, today)} · ${LAST_CONTACT_LABELS[last.kind]}`
}

/** "Bellen · di 20 okt"; overdue "Rapportage · ! 9 okt · 8 dagen te laat" (never colour alone). */
export function nextActionText(next: CrmNextAction, today: string = todayIso()): string {
  const title = next.kind === 'deadline' ? `Deadline: ${next.title}` : next.title
  if (!next.due_date) return title
  if (!next.overdue) return `${title} · ${weekdayDay(next.due_date, today)}`
  const late = overdueText(next.due_date, today)
  return [title, `! ${shortDay(next.due_date, today)}`, late].filter(Boolean).join(' · ')
}

/** "16 okt 14:02 · Jesse (gesprek) · via Anna" (via only on an organisation's activity logged on a person): E2's marker in the Systeem section (addendum C.2). */
export function lastActivityText(last: CrmLastActivity, today: string = todayIso()): string {
  const when = `${shortDay(amsterdamDay(last.at), today)} ${timeOfDay(last.at)}`
  const who = last.by ? ` · ${last.by.full_name}` : ''
  const via = last.via ? ` · via ${last.via.full_name}` : ''
  return `${when}${who} (${last.label.toLowerCase()})${via}`
}
